import { db, storage, isFirebaseConfigured } from './firebase'
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, setDoc,
  query, where, orderBy, limit, onSnapshot, serverTimestamp,
  getCountFromServer
} from 'firebase/firestore'
import {
  ref, uploadBytes, getDownloadURL, deleteObject
} from 'firebase/storage'

// Helper: generate a Firestore-compatible timestamp for created_at / updated_at
const now = () => new Date().toISOString()

// Helper: convert Firestore doc to plain object with id
const docToObj = (docSnap) => {
  if (!docSnap.exists()) return null
  return { id: docSnap.id, ...docSnap.data() }
}

// Helper: convert query snapshot to array
const snapToArray = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data() }))

// ============================================
// USER PROFILE MANAGEMENT
// ============================================

export const syncUserProfile = async (firebaseUser) => {
  if (!firebaseUser || !isFirebaseConfigured) return null

  try {
    // Users are keyed by firebase_uid
    const userRef = doc(db, 'users', firebaseUser.uid)
    const userSnap = await getDoc(userRef)

    if (userSnap.exists()) {
      const existingUser = { id: userSnap.id, ...userSnap.data() }
      if (!existingUser.role) {
        await updateDoc(userRef, { role: 'owner', updated_at: now() })
        return { ...existingUser, role: 'owner' }
      }
      return existingUser
    }

    // Create new user
    const newUser = {
      firebase_uid: firebaseUser.uid,
      email: firebaseUser.email,
      full_name: firebaseUser.displayName || '',
      avatar_url: firebaseUser.photoURL || '',
      role: 'owner',
      phone: '',
      created_at: now(),
      updated_at: now()
    }
    await setDoc(userRef, newUser)
    return { id: firebaseUser.uid, ...newUser }
  } catch (error) {
    console.error('Error syncing user profile:', error)
    return null
  }
}

export const updateUserProfile = async (userId, updates) => {
  if (!isFirebaseConfigured) return null
  try {
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, { ...updates, updated_at: now() })
    const snap = await getDoc(userRef)
    return docToObj(snap)
  } catch (error) {
    console.error('Error updating user profile:', error)
    return null
  }
}

export const getUserById = async (userId) => {
  if (!isFirebaseConfigured) return null
  try {
    const userRef = doc(db, 'users', userId)
    const snap = await getDoc(userRef)
    return docToObj(snap)
  } catch (error) {
    console.error('Error fetching user:', error)
    return null
  }
}

export const deleteUserData = async (userId) => {
  if (!isFirebaseConfigured) return { error: null }

  try {
    // Get all user's yachts
    const yachtsQ = query(collection(db, 'yachts'), where('user_id', '==', userId))
    const yachtsSnap = await getDocs(yachtsQ)

    for (const yachtDoc of yachtsSnap.docs) {
      const yachtId = yachtDoc.id
      // Delete all subcollections
      const subcollections = [
        'maintenance_logs', 'crew_members', 'invoices', 'documents',
        'equipment', 'bookings', 'guests', 'issue_reports', 'inventory_items',
        'inventory_categories', 'orders', 'checklists', 'yacht_crew',
        'crew_schedules', 'time_off_requests', 'certifications',
        'maintenance_schedule', 'handovers', 'handover_items',
        'order_items', 'reports'
      ]
      for (const sub of subcollections) {
        const subSnap = await getDocs(collection(db, 'yachts', yachtId, sub))
        for (const d of subSnap.docs) {
          await deleteDoc(d.ref)
        }
      }
      await deleteDoc(yachtDoc.ref)
    }

    // Delete invitations by user
    const invQ = query(collection(db, 'invitations'), where('invited_by', '==', userId))
    const invSnap = await getDocs(invQ)
    for (const d of invSnap.docs) await deleteDoc(d.ref)

    // Delete user profile
    await deleteDoc(doc(db, 'users', userId))

    return { error: null }
  } catch (error) {
    console.error('Error deleting user data:', error)
    return { error: error.message }
  }
}

// ============================================
// YACHT OPERATIONS
// ============================================

export const getUserYachts = async (userId) => {
  if (!isFirebaseConfigured) return []
  try {
    const q = query(
      collection(db, 'yachts'),
      where('user_id', '==', userId),
      orderBy('created_at', 'desc')
    )
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching user yachts:', error)
    return []
  }
}

export const getYachts = getUserYachts

// Plan yacht limits
const PLAN_YACHT_LIMITS = {
  free: 1,
  captain: 3,
  professional: 5,
  fleet: 10,
  enterprise: -1  // unlimited
}

export const createYacht = async (yacht) => {
  if (!isFirebaseConfigured) return null
  try {
    const userId = yacht.user_id
    if (!userId) throw new Error('user_id is required')

    // Check plan limits
    const userDoc = await getDoc(doc(db, 'users', userId))
    const plan = userDoc.exists() ? (userDoc.data().subscription_plan || 'free') : 'free'
    const limit = PLAN_YACHT_LIMITS[plan] ?? 1

    if (limit !== -1) {
      // Count existing yachts
      const yachtsQ = query(collection(db, 'yachts'), where('user_id', '==', userId))
      const yachtsSnap = await getDocs(yachtsQ)
      const currentCount = yachtsSnap.size

      if (currentCount >= limit) {
        const error = new Error('Yacht limit reached for your current plan')
        error.code = 'YACHT_LIMIT_REACHED'
        error.plan = plan
        error.limit = limit
        error.currentCount = currentCount
        throw error
      }
    }

    const docRef = await addDoc(collection(db, 'yachts'), {
      ...yacht,
      created_at: now(),
      updated_at: now()
    })
    return { id: docRef.id, ...yacht, created_at: now(), updated_at: now() }
  } catch (error) {
    if (error.code === 'YACHT_LIMIT_REACHED') throw error
    console.error('Error creating yacht:', error)
    return null
  }
}

export const updateYacht = async (yachtId, updates) => {
  if (!isFirebaseConfigured) return null
  try {
    const ref = doc(db, 'yachts', yachtId)
    await updateDoc(ref, { ...updates, updated_at: now() })
    const snap = await getDoc(ref)
    return docToObj(snap)
  } catch (error) {
    console.error('Error updating yacht:', error)
    return null
  }
}

export const deleteYacht = async (yachtId) => {
  if (!isFirebaseConfigured) return false
  try {
    await deleteDoc(doc(db, 'yachts', yachtId))
    return true
  } catch (error) {
    console.error('Error deleting yacht:', error)
    return false
  }
}

// ============================================
// GENERIC SUBCOLLECTION HELPERS
// ============================================

const getSubcollection = async (yachtId, subcol, orderField, ascending = true) => {
  if (!isFirebaseConfigured) return []
  try {
    const q = query(
      collection(db, 'yachts', yachtId, subcol),
      orderBy(orderField, ascending ? 'asc' : 'desc')
    )
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error(`Error fetching ${subcol}:`, error)
    return []
  }
}

const createInSubcollection = async (yachtId, subcol, data) => {
  if (!isFirebaseConfigured) return null
  try {
    const docRef = await addDoc(collection(db, 'yachts', yachtId, subcol), {
      ...data,
      created_at: now(),
      updated_at: now()
    })
    return { id: docRef.id, ...data, created_at: now(), updated_at: now() }
  } catch (error) {
    console.error(`Error creating ${subcol}:`, error)
    return null
  }
}

const updateInSubcollection = async (yachtId, subcol, docId, updates) => {
  if (!isFirebaseConfigured) return null
  try {
    const ref = doc(db, 'yachts', yachtId, subcol, docId)
    await updateDoc(ref, { ...updates, updated_at: now() })
    const snap = await getDoc(ref)
    return docToObj(snap)
  } catch (error) {
    console.error(`Error updating ${subcol}:`, error)
    return null
  }
}

const deleteFromSubcollection = async (yachtId, subcol, docId) => {
  if (!isFirebaseConfigured) return false
  try {
    await deleteDoc(doc(db, 'yachts', yachtId, subcol, docId))
    return true
  } catch (error) {
    console.error(`Error deleting from ${subcol}:`, error)
    return false
  }
}

// ============================================
// MAINTENANCE LOGS
// ============================================

export const getMaintenanceLogs = async (yachtId) => {
  return getSubcollection(yachtId, 'maintenance_logs', 'due_date', true)
}

export const createMaintenanceLog = async (log) => {
  const { yacht_id, ...data } = log
  return createInSubcollection(yacht_id, 'maintenance_logs', { ...data, yacht_id })
}

export const updateMaintenanceLog = async (id, updates) => {
  if (!isFirebaseConfigured) return null
  // We need yacht_id to find the subcollection doc. It should be in updates or we search.
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'maintenance_logs', id, cleanUpdates)
  }
  // Fallback: search across yachts (shouldn't happen in practice)
  console.error('updateMaintenanceLog: yacht_id required')
  return null
}

export const deleteMaintenanceLog = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'maintenance_logs', id)
  console.error('deleteMaintenanceLog: yacht_id required')
  return false
}

// ============================================
// VENDORS (top-level collection)
// ============================================

export const getVendors = async (location) => {
  if (!isFirebaseConfigured) return []
  try {
    let q
    if (location) {
      q = query(collection(db, 'vendors'), where('location', '==', location), orderBy('rating', 'desc'))
    } else {
      q = query(collection(db, 'vendors'), orderBy('rating', 'desc'))
    }
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching vendors:', error)
    return []
  }
}

export const createVendor = async (vendor) => {
  if (!isFirebaseConfigured) return null
  try {
    const docRef = await addDoc(collection(db, 'vendors'), { ...vendor, created_at: now(), updated_at: now() })
    return { id: docRef.id, ...vendor }
  } catch (error) {
    console.error('Error creating vendor:', error)
    return null
  }
}

export const updateVendor = async (id, updates) => {
  if (!isFirebaseConfigured) return null
  try {
    const ref = doc(db, 'vendors', id)
    await updateDoc(ref, { ...updates, updated_at: now() })
    const snap = await getDoc(ref)
    return docToObj(snap)
  } catch (error) {
    console.error('Error updating vendor:', error)
    return null
  }
}

export const deleteVendor = async (id) => {
  if (!isFirebaseConfigured) return false
  try {
    await deleteDoc(doc(db, 'vendors', id))
    return true
  } catch (error) {
    console.error('Error deleting vendor:', error)
    return false
  }
}

// ============================================
// DOCUMENTS
// ============================================

export const getDocuments = async (yachtId) => {
  return getSubcollection(yachtId, 'documents', 'created_at', false)
}

export const createDocument = async (docData) => {
  const { yacht_id, ...data } = docData
  return createInSubcollection(yacht_id, 'documents', { ...data, yacht_id })
}

export const updateDocument = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'documents', id, cleanUpdates)
  }
  console.error('updateDocument: yacht_id required')
  return null
}

export const deleteDocument = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'documents', id)
  console.error('deleteDocument: yacht_id required')
  return false
}

// ============================================
// CHECKLISTS
// ============================================

export const getChecklists = async (yachtId) => {
  return getSubcollection(yachtId, 'checklists', 'name', true)
}

export const createChecklist = async (checklist) => {
  const { yacht_id, ...data } = checklist
  return createInSubcollection(yacht_id, 'checklists', { ...data, yacht_id })
}

export const updateChecklist = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'checklists', id, cleanUpdates)
  }
  console.error('updateChecklist: yacht_id required')
  return null
}

export const deleteChecklist = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'checklists', id)
  console.error('deleteChecklist: yacht_id required')
  return false
}

// ============================================
// LANDING PAGE
// ============================================

export const getRandomLandingYachts = async (count = 3) => {
  if (!isFirebaseConfigured) return []
  try {
    const snap = await getDocs(collection(db, 'yacht_landing'))
    const data = snapToArray(snap)
    const shuffled = [...data].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, count)
  } catch (error) {
    console.error('Error fetching landing yachts:', error)
    return []
  }
}

// ============================================
// CREW MEMBERS
// ============================================

export const getCrewMembers = async (yachtId) => {
  return getSubcollection(yachtId, 'crew_members', 'name', true)
}

export const createCrewMember = async (member) => {
  const { yacht_id, ...data } = member
  return createInSubcollection(yacht_id, 'crew_members', { ...data, yacht_id })
}

export const updateCrewMember = async (memberId, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'crew_members', memberId, cleanUpdates)
  }
  console.error('updateCrewMember: yacht_id required')
  return null
}

export const deleteCrewMember = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'crew_members', id)
  console.error('deleteCrewMember: yacht_id required')
  return false
}

// ============================================
// INVOICES
// ============================================

export const getInvoices = async (yachtId, status = null) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [orderBy('created_at', 'desc')]
    if (status) constraints.unshift(where('status', '==', status))
    const q = query(collection(db, 'yachts', yachtId, 'invoices'), ...constraints)
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching invoices:', error)
    return []
  }
}

export const createInvoice = async (invoice) => {
  const { yacht_id, ...data } = invoice
  return createInSubcollection(yacht_id, 'invoices', { ...data, yacht_id })
}

export const updateInvoice = async (invoiceId, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'invoices', invoiceId, cleanUpdates)
  }
  console.error('updateInvoice: yacht_id required')
  return null
}

export const deleteInvoice = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'invoices', id)
  console.error('deleteInvoice: yacht_id required')
  return false
}

// ============================================
// EQUIPMENT
// ============================================

export const getEquipment = async (yachtId) => {
  return getSubcollection(yachtId, 'equipment', 'name', true)
}

export const createEquipment = async (equipment) => {
  const { yacht_id, ...data } = equipment
  return createInSubcollection(yacht_id, 'equipment', { ...data, yacht_id })
}

export const updateEquipment = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'equipment', id, cleanUpdates)
  }
  console.error('updateEquipment: yacht_id required')
  return null
}

export const deleteEquipment = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'equipment', id)
  console.error('deleteEquipment: yacht_id required')
  return false
}

// ============================================
// BOOKINGS
// ============================================

export const getBookings = async (yachtId) => {
  if (!isFirebaseConfigured) return []
  try {
    const q = query(
      collection(db, 'yachts', yachtId, 'bookings'),
      orderBy('start_date', 'asc')
    )
    const snap = await getDocs(q)
    const bookings = snapToArray(snap)

    // Fetch linked guests for each booking (denormalized guest data)
    // In Firestore we store guest data inline or fetch from guests subcollection
    for (const booking of bookings) {
      if (booking.guest_id) {
        try {
          const guestRef = doc(db, 'yachts', yachtId, 'guests', booking.guest_id)
          const guestSnap = await getDoc(guestRef)
          if (guestSnap.exists()) {
            booking.guests = { id: guestSnap.id, ...guestSnap.data() }
          }
        } catch (e) {
          // Guest may not exist
        }
      }
    }
    return bookings
  } catch (error) {
    console.error('Error fetching bookings:', error)
    return []
  }
}

export const createBooking = async (booking) => {
  if (!isFirebaseConfigured) return { data: null, error: 'Firebase not configured' }
  try {
    const { yacht_id, ...data } = booking
    const docRef = await addDoc(collection(db, 'yachts', yacht_id, 'bookings'), {
      ...data, yacht_id,
      created_at: now(),
      updated_at: now()
    })
    return { data: { id: docRef.id, ...data, yacht_id }, error: null }
  } catch (error) {
    console.error('Error creating booking:', error)
    return { data: null, error: error.message || 'Failed to create booking' }
  }
}

export const updateBooking = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'bookings', id, cleanUpdates)
  }
  console.error('updateBooking: yacht_id required')
  return null
}

export const deleteBooking = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'bookings', id)
  console.error('deleteBooking: yacht_id required')
  return false
}

// ============================================
// GUESTS
// ============================================

export const getGuests = async (yachtId) => {
  return getSubcollection(yachtId, 'guests', 'last_name', true)
}

export const createGuest = async (guest) => {
  if (!isFirebaseConfigured) return null
  try {
    const { yacht_id, ...data } = guest
    const docRef = await addDoc(collection(db, 'yachts', yacht_id, 'guests'), {
      ...data, yacht_id,
      created_at: now(),
      updated_at: now()
    })
    return { id: docRef.id, ...data, yacht_id }
  } catch (error) {
    console.error('Error creating guest:', error)
    throw new Error(error.message || 'Failed to create guest')
  }
}

export const updateGuest = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'guests', id, cleanUpdates)
  }
  console.error('updateGuest: yacht_id required')
  return null
}

export const deleteGuest = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'guests', id)
  console.error('deleteGuest: yacht_id required')
  return false
}

// ============================================
// ISSUE REPORTS
// ============================================

export const getIssueReports = async (yachtId) => {
  if (!isFirebaseConfigured) return []
  try {
    const q = query(
      collection(db, 'yachts', yachtId, 'issue_reports'),
      orderBy('created_at', 'desc')
    )
    const snap = await getDocs(q)
    const reports = snapToArray(snap)

    // Fetch reporter info
    for (const report of reports) {
      if (report.reported_by) {
        try {
          const userSnap = await getDoc(doc(db, 'users', report.reported_by))
          if (userSnap.exists()) {
            const u = userSnap.data()
            report.reporter = { id: userSnap.id, full_name: u.full_name, email: u.email }
          }
        } catch (e) { /* ignore */ }
      }
    }
    return reports
  } catch (error) {
    console.error('Error fetching issue reports:', error)
    return []
  }
}

export const createIssueReport = async (report) => {
  const { yacht_id, ...data } = report
  return createInSubcollection(yacht_id, 'issue_reports', { ...data, yacht_id })
}

export const updateIssueReport = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'issue_reports', id, cleanUpdates)
  }
  console.error('updateIssueReport: yacht_id required')
  return null
}

export const deleteIssueReport = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'issue_reports', id)
  console.error('deleteIssueReport: yacht_id required')
  return false
}

// ============================================
// DASHBOARD STATS
// ============================================

export const getDashboardStats = async (yachtId) => {
  if (!isFirebaseConfigured) return null
  try {
    // Count pending maintenance
    const maintQ = query(
      collection(db, 'yachts', yachtId, 'maintenance_logs'),
      where('status', '==', 'pending')
    )
    const issuesQ = query(
      collection(db, 'yachts', yachtId, 'issue_reports'),
      where('status', '==', 'open')
    )
    const invQ = query(
      collection(db, 'yachts', yachtId, 'invoices'),
      where('status', '==', 'pending')
    )
    const crewCol = collection(db, 'yachts', yachtId, 'crew_members')

    const [maintSnap, issuesSnap, invSnap, crewSnap] = await Promise.all([
      getDocs(maintQ),
      getDocs(issuesQ),
      getDocs(invQ),
      getDocs(crewCol)
    ])

    return {
      pendingMaintenance: maintSnap.size,
      openIssues: issuesSnap.size,
      pendingInvoices: invSnap.size,
      crewCount: crewSnap.size
    }
  } catch (error) {
    console.error('Error fetching dashboard stats:', error)
    return { pendingMaintenance: 0, openIssues: 0, pendingInvoices: 0, crewCount: 0 }
  }
}

// ============================================
// CERTIFICATIONS
// ============================================

export const getCertifications = async (crewMemberId, yachtId) => {
  if (!isFirebaseConfigured) return []
  try {
    // Certifications stored under yachts/{yachtId}/certifications with crew_member_id field
    // If yachtId not provided, we can't query. In practice the caller should pass it.
    if (!yachtId) {
      console.error('getCertifications: yachtId required')
      return []
    }
    const q = query(
      collection(db, 'yachts', yachtId, 'certifications'),
      where('crew_member_id', '==', crewMemberId),
      orderBy('expiry_date', 'asc')
    )
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching certifications:', error)
    return []
  }
}

export const createCertification = async (cert) => {
  const { yacht_id, ...data } = cert
  if (!yacht_id) {
    console.error('createCertification: yacht_id required')
    return null
  }
  return createInSubcollection(yacht_id, 'certifications', { ...data, yacht_id })
}

export const updateCertification = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'certifications', id, cleanUpdates)
  }
  console.error('updateCertification: yacht_id required')
  return null
}

export const deleteCertification = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'certifications', id)
  console.error('deleteCertification: yacht_id required')
  return false
}

// ============================================
// CREW SCHEDULES
// ============================================

export const getCrewSchedules = async (yachtId, startDate, endDate) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [orderBy('date', 'asc')]
    if (startDate) constraints.unshift(where('date', '>=', startDate))
    if (endDate) constraints.push(where('date', '<=', endDate))

    const q = query(collection(db, 'yachts', yachtId, 'crew_schedules'), ...constraints)
    const snap = await getDocs(q)
    const schedules = snapToArray(snap)

    // Fetch crew member info
    for (const sched of schedules) {
      if (sched.crew_member_id) {
        try {
          const cmRef = doc(db, 'yachts', yachtId, 'crew_members', sched.crew_member_id)
          const cmSnap = await getDoc(cmRef)
          if (cmSnap.exists()) {
            const cm = cmSnap.data()
            sched.crew_members = { name: cm.name, role: cm.role }
          }
        } catch (e) { /* ignore */ }
      }
    }
    return schedules
  } catch (error) {
    console.error('Error fetching crew schedules:', error)
    return []
  }
}

export const createCrewSchedule = async (schedule) => {
  const { yacht_id, ...data } = schedule
  return createInSubcollection(yacht_id, 'crew_schedules', { ...data, yacht_id })
}

export const updateCrewSchedule = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'crew_schedules', id, cleanUpdates)
  }
  console.error('updateCrewSchedule: yacht_id required')
  return null
}

export const deleteCrewSchedule = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'crew_schedules', id)
  console.error('deleteCrewSchedule: yacht_id required')
  return false
}

// ============================================
// TIME OFF REQUESTS
// ============================================

export const getTimeOffRequests = async (yachtId, status = null) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [orderBy('start_date', 'asc')]
    if (status) constraints.unshift(where('status', '==', status))

    const q = query(collection(db, 'yachts', yachtId, 'time_off_requests'), ...constraints)
    const snap = await getDocs(q)
    const requests = snapToArray(snap)

    // Fetch crew member info
    for (const req of requests) {
      if (req.crew_member_id) {
        try {
          const cmRef = doc(db, 'yachts', yachtId, 'crew_members', req.crew_member_id)
          const cmSnap = await getDoc(cmRef)
          if (cmSnap.exists()) {
            const cm = cmSnap.data()
            req.crew_members = { name: cm.name, role: cm.role }
          }
        } catch (e) { /* ignore */ }
      }
    }
    return requests
  } catch (error) {
    console.error('Error fetching time off requests:', error)
    return []
  }
}

export const createTimeOffRequest = async (request) => {
  const { yacht_id, ...data } = request
  return createInSubcollection(yacht_id, 'time_off_requests', { ...data, yacht_id })
}

export const updateTimeOffRequest = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'time_off_requests', id, cleanUpdates)
  }
  console.error('updateTimeOffRequest: yacht_id required')
  return null
}

export const deleteTimeOffRequest = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'time_off_requests', id)
  console.error('deleteTimeOffRequest: yacht_id required')
  return false
}

// ============================================
// INVENTORY CATEGORIES
// ============================================

export const getInventoryCategories = async (yachtId) => {
  return getSubcollection(yachtId, 'inventory_categories', 'sort_order', true)
}

export const createInventoryCategory = async (category) => {
  const { yacht_id, ...data } = category
  return createInSubcollection(yacht_id, 'inventory_categories', { ...data, yacht_id })
}

export const updateInventoryCategory = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'inventory_categories', id, cleanUpdates)
  }
  console.error('updateInventoryCategory: yacht_id required')
  return null
}

export const deleteInventoryCategory = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'inventory_categories', id)
  console.error('deleteInventoryCategory: yacht_id required')
  return false
}

// ============================================
// INVENTORY ITEMS
// ============================================

export const getInventoryItems = async (yachtId, categoryId = null) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [orderBy('name', 'asc')]
    if (categoryId) constraints.unshift(where('category_id', '==', categoryId))

    const q = query(collection(db, 'yachts', yachtId, 'inventory_items'), ...constraints)
    const snap = await getDocs(q)
    const items = snapToArray(snap)

    // Fetch category info
    for (const item of items) {
      if (item.category_id) {
        try {
          const catRef = doc(db, 'yachts', yachtId, 'inventory_categories', item.category_id)
          const catSnap = await getDoc(catRef)
          if (catSnap.exists()) {
            const cat = catSnap.data()
            item.inventory_categories = { name: cat.name, icon: cat.icon }
          }
        } catch (e) { /* ignore */ }
      }
    }
    return items
  } catch (error) {
    console.error('Error fetching inventory items:', error)
    return []
  }
}

export const createInventoryItem = async (item) => {
  const { yacht_id, ...data } = item
  return createInSubcollection(yacht_id, 'inventory_items', { ...data, yacht_id })
}

export const updateInventoryItem = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'inventory_items', id, cleanUpdates)
  }
  console.error('updateInventoryItem: yacht_id required')
  return null
}

export const deleteInventoryItem = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'inventory_items', id)
  console.error('deleteInventoryItem: yacht_id required')
  return false
}

// ============================================
// ORDERS
// ============================================

export const getOrders = async (yachtId, status = null) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [orderBy('created_at', 'desc')]
    if (status) constraints.unshift(where('status', '==', status))
    const q = query(collection(db, 'yachts', yachtId, 'orders'), ...constraints)
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching orders:', error)
    return []
  }
}

export const createOrder = async (order) => {
  const { yacht_id, ...data } = order
  return createInSubcollection(yacht_id, 'orders', { ...data, yacht_id })
}

export const updateOrder = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'orders', id, cleanUpdates)
  }
  console.error('updateOrder: yacht_id required')
  return null
}

export const deleteOrder = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'orders', id)
  console.error('deleteOrder: yacht_id required')
  return false
}

// ============================================
// ORDER ITEMS (subcollection of orders under yacht)
// ============================================

export const getOrderItems = async (orderId, yachtId) => {
  if (!isFirebaseConfigured || !yachtId) return []
  try {
    const q = query(
      collection(db, 'yachts', yachtId, 'order_items'),
      where('order_id', '==', orderId),
      orderBy('name', 'asc')
    )
    const snap = await getDocs(q)
    const items = snapToArray(snap)

    // Fetch inventory item name
    for (const item of items) {
      if (item.inventory_item_id) {
        try {
          const invRef = doc(db, 'yachts', yachtId, 'inventory_items', item.inventory_item_id)
          const invSnap = await getDoc(invRef)
          if (invSnap.exists()) {
            item.inventory_items = { name: invSnap.data().name }
          }
        } catch (e) { /* ignore */ }
      }
    }
    return items
  } catch (error) {
    console.error('Error fetching order items:', error)
    return []
  }
}

export const createOrderItem = async (item) => {
  const { yacht_id, ...data } = item
  if (!yacht_id) {
    console.error('createOrderItem: yacht_id required')
    return null
  }
  return createInSubcollection(yacht_id, 'order_items', { ...data, yacht_id })
}

export const updateOrderItem = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'order_items', id, cleanUpdates)
  }
  console.error('updateOrderItem: yacht_id required')
  return null
}

export const deleteOrderItem = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'order_items', id)
  console.error('deleteOrderItem: yacht_id required')
  return false
}

// ============================================
// MAINTENANCE SCHEDULE
// ============================================

export const getMaintenanceSchedule = async (yachtId, activeOnly = true) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [orderBy('next_due', 'asc')]
    if (activeOnly) constraints.unshift(where('is_active', '==', true))

    const q = query(collection(db, 'yachts', yachtId, 'maintenance_schedule'), ...constraints)
    const snap = await getDocs(q)
    const schedules = snapToArray(snap)

    // Fetch equipment info
    for (const sched of schedules) {
      if (sched.equipment_id) {
        try {
          const eqRef = doc(db, 'yachts', yachtId, 'equipment', sched.equipment_id)
          const eqSnap = await getDoc(eqRef)
          if (eqSnap.exists()) {
            const eq = eqSnap.data()
            sched.equipment = { name: eq.name, category: eq.category }
          }
        } catch (e) { /* ignore */ }
      }
    }
    return schedules
  } catch (error) {
    console.error('Error fetching maintenance schedule:', error)
    return []
  }
}

// ============================================
// HANDOVERS
// ============================================

export const getHandovers = async (yachtId, bookingId = null) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [orderBy('scheduled_date', 'asc')]
    if (bookingId) constraints.unshift(where('booking_id', '==', bookingId))

    const q = query(collection(db, 'yachts', yachtId, 'handovers'), ...constraints)
    const snap = await getDocs(q)
    const handovers = snapToArray(snap)

    // Fetch booking info
    for (const h of handovers) {
      if (h.booking_id) {
        try {
          const bRef = doc(db, 'yachts', yachtId, 'bookings', h.booking_id)
          const bSnap = await getDoc(bRef)
          if (bSnap.exists()) {
            const b = bSnap.data()
            const bookingData = { start_date: b.start_date, end_date: b.end_date }
            if (b.guest_id) {
              try {
                const gRef = doc(db, 'yachts', yachtId, 'guests', b.guest_id)
                const gSnap = await getDoc(gRef)
                if (gSnap.exists()) {
                  const g = gSnap.data()
                  bookingData.guests = { first_name: g.first_name, last_name: g.last_name }
                }
              } catch (e) { /* ignore */ }
            }
            h.bookings = bookingData
          }
        } catch (e) { /* ignore */ }
      }
    }
    return handovers
  } catch (error) {
    console.error('Error fetching handovers:', error)
    return []
  }
}

export const createHandover = async (handover) => {
  const { yacht_id, ...data } = handover
  return createInSubcollection(yacht_id, 'handovers', { ...data, yacht_id })
}

export const updateHandover = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'handovers', id, cleanUpdates)
  }
  console.error('updateHandover: yacht_id required')
  return null
}

export const deleteHandover = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'handovers', id)
  console.error('deleteHandover: yacht_id required')
  return false
}

// ============================================
// HANDOVER ITEMS
// ============================================

export const getHandoverItems = async (handoverId, yachtId) => {
  if (!isFirebaseConfigured || !yachtId) return []
  try {
    const q = query(
      collection(db, 'yachts', yachtId, 'handover_items'),
      where('handover_id', '==', handoverId),
      orderBy('category', 'asc')
    )
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching handover items:', error)
    return []
  }
}

export const createHandoverItem = async (item) => {
  const { yacht_id, ...data } = item
  if (!yacht_id) {
    console.error('createHandoverItem: yacht_id required')
    return null
  }
  return createInSubcollection(yacht_id, 'handover_items', { ...data, yacht_id })
}

export const updateHandoverItem = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'handover_items', id, cleanUpdates)
  }
  console.error('updateHandoverItem: yacht_id required')
  return null
}

export const deleteHandoverItem = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'handover_items', id)
  console.error('deleteHandoverItem: yacht_id required')
  return false
}

// ============================================
// REPORTS
// ============================================

export const getReports = async (yachtId, type = null) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [orderBy('created_at', 'desc')]
    if (type) constraints.unshift(where('type', '==', type))
    const q = query(collection(db, 'yachts', yachtId, 'reports'), ...constraints)
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching reports:', error)
    return []
  }
}

export const createReport = async (report) => {
  const { yacht_id, ...data } = report
  return createInSubcollection(yacht_id, 'reports', { ...data, yacht_id })
}

export const updateReport = async (id, updates) => {
  const yachtId = updates._yachtId || updates.yacht_id
  if (yachtId) {
    const { _yachtId, ...cleanUpdates } = updates
    return updateInSubcollection(yachtId, 'reports', id, cleanUpdates)
  }
  console.error('updateReport: yacht_id required')
  return null
}

export const deleteReport = async (id, yachtId) => {
  if (yachtId) return deleteFromSubcollection(yachtId, 'reports', id)
  console.error('deleteReport: yacht_id required')
  return false
}

// ============================================
// INVITATIONS (top-level collection)
// ============================================

export const getInvitations = async (yachtId, status = null) => {
  if (!isFirebaseConfigured) return []
  try {
    const constraints = [where('yacht_id', '==', yachtId), orderBy('created_at', 'desc')]
    if (status) constraints.push(where('status', '==', status))

    const q = query(collection(db, 'invitations'), ...constraints)
    const snap = await getDocs(q)
    const invitations = snapToArray(snap)

    // Fetch inviter info
    for (const inv of invitations) {
      if (inv.invited_by) {
        try {
          const userSnap = await getDoc(doc(db, 'users', inv.invited_by))
          if (userSnap.exists()) {
            const u = userSnap.data()
            inv.users = { full_name: u.full_name, email: u.email }
          }
        } catch (e) { /* ignore */ }
      }
    }
    return invitations
  } catch (error) {
    console.error('Error fetching invitations:', error)
    return []
  }
}

export const createInvitation = async (invitation) => {
  if (!isFirebaseConfigured) return null
  try {
    const docRef = await addDoc(collection(db, 'invitations'), { ...invitation, created_at: now() })
    return { id: docRef.id, ...invitation }
  } catch (error) {
    console.error('Error creating invitation:', error)
    return null
  }
}

export const updateInvitation = async (id, updates) => {
  if (!isFirebaseConfigured) return null
  try {
    const ref = doc(db, 'invitations', id)
    await updateDoc(ref, updates)
    const snap = await getDoc(ref)
    return docToObj(snap)
  } catch (error) {
    console.error('Error updating invitation:', error)
    return null
  }
}

export const deleteInvitation = async (id) => {
  if (!isFirebaseConfigured) return false
  try {
    await deleteDoc(doc(db, 'invitations', id))
    return true
  } catch (error) {
    console.error('Error deleting invitation:', error)
    return false
  }
}

// ============================================
// FILE UPLOAD (Firebase Storage)
// ============================================

export const uploadFile = async (bucket, path, file) => {
  console.log('[UPLOAD] Starting upload to path:', path)
  console.log('[UPLOAD] File info:', { name: file.name, type: file.type, size: file.size })

  if (!isFirebaseConfigured || !storage) {
    console.error('[UPLOAD] Firebase not configured')
    return { error: 'Firebase not configured' }
  }

  try {
    // In Firebase Storage, "bucket" is mapped to a folder prefix
    const storagePath = `${bucket}/${path}`
    const storageRef = ref(storage, storagePath)
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
      cacheControl: 'public, max-age=3600'
    })

    const publicUrl = await getDownloadURL(snapshot.ref)
    console.log('[UPLOAD] Public URL:', publicUrl)

    return { data: { fullPath: snapshot.ref.fullPath, publicUrl }, error: null }
  } catch (err) {
    console.error('[UPLOAD] Exception:', err)
    return { error: err.message || 'Upload exception occurred' }
  }
}

export const deleteFile = async (bucket, path) => {
  if (!isFirebaseConfigured || !storage) return false
  try {
    const storagePath = `${bucket}/${path}`
    const storageRef = ref(storage, storagePath)
    await deleteObject(storageRef)
    return true
  } catch (error) {
    console.error('Error deleting file:', error)
    return false
  }
}

export const getFileUrl = async (bucket, path) => {
  if (!isFirebaseConfigured || !storage) return null
  try {
    const storagePath = `${bucket}/${path}`
    const storageRef = ref(storage, storagePath)
    return await getDownloadURL(storageRef)
  } catch (error) {
    console.error('Error getting file URL:', error)
    return null
  }
}

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

export const subscribeToMaintenanceLogs = (yachtId, callback) => {
  if (!isFirebaseConfigured) return { unsubscribe: () => {} }
  const q = query(collection(db, 'yachts', yachtId, 'maintenance_logs'))
  const unsubscribe = onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      callback({
        eventType: change.type, // 'added', 'modified', 'removed'
        new: change.type !== 'removed' ? { id: change.doc.id, ...change.doc.data() } : null,
        old: change.type === 'removed' ? { id: change.doc.id, ...change.doc.data() } : null
      })
    })
  })
  return { unsubscribe }
}

// ============================================
// DIRECT FIRESTORE ACCESS (for Settings.jsx etc)
// replaces raw supabase.from(...) calls
// ============================================

export const getYachtWithOwner = async (yachtId) => {
  if (!isFirebaseConfigured) return null
  try {
    const yachtSnap = await getDoc(doc(db, 'yachts', yachtId))
    if (!yachtSnap.exists()) return null
    const yacht = { id: yachtSnap.id, ...yachtSnap.data() }

    if (yacht.user_id) {
      const userSnap = await getDoc(doc(db, 'users', yacht.user_id))
      if (userSnap.exists()) {
        yacht.users = { id: userSnap.id, ...userSnap.data() }
      }
    }
    return yacht
  } catch (error) {
    console.error('Error fetching yacht with owner:', error)
    return null
  }
}

export const getYachtCrew = async (yachtId) => {
  if (!isFirebaseConfigured) return []
  try {
    const q = query(collection(db, 'yacht_crew'), where('yacht_id', '==', yachtId))
    const snap = await getDocs(q)
    const crew = snapToArray(snap)

    for (const c of crew) {
      if (c.user_id) {
        try {
          const userSnap = await getDoc(doc(db, 'users', c.user_id))
          if (userSnap.exists()) {
            c.users = { id: userSnap.id, ...userSnap.data() }
          }
        } catch (e) { /* ignore */ }
      }
    }
    return crew
  } catch (error) {
    console.error('Error fetching yacht crew:', error)
    return []
  }
}

export const updateUserRole = async (userId, newRole) => {
  if (!isFirebaseConfigured) return { error: 'Not configured' }
  try {
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, { role: newRole, updated_at: now() })
    return { error: null }
  } catch (error) {
    console.error('Error updating user role:', error)
    return { error: error.message }
  }
}

// Subscription status (for Stripe integration)
export const getSubscriptionStatus = async (userId) => {
  if (!isFirebaseConfigured) return null
  try {
    const q = query(
      collection(db, 'subscriptions'),
      where('user_id', '==', userId),
      where('status', '==', 'active')
    )
    const snap = await getDocs(q)
    if (snap.empty) return null
    return { id: snap.docs[0].id, ...snap.docs[0].data() }
  } catch (error) {
    console.error('Error fetching subscription:', error)
    return null
  }
}

// Update user plan (for Stripe)
export const updateUserPlan = async (userId, planId) => {
  if (!isFirebaseConfigured) return { error: 'Not configured' }
  try {
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, { subscription_plan: planId, updated_at: now() })
    return { success: true }
  } catch (error) {
    console.error('Error updating user plan:', error)
    return { error: error.message }
  }
}

// ============================================
// PREVENTIVE MAINTENANCE SCHEDULING (PMS)
// ============================================

export const getMaintenanceSchedules = async (yachtId) => {
  return getSubcollection(yachtId, 'maintenance_schedules', 'created_at', false)
}

// Supports both: createMaintenanceSchedule(yachtId, schedule) and createMaintenanceSchedule(scheduleWithYachtId)
export const createMaintenanceSchedule = async (yachtIdOrSchedule, schedule) => {
  if (schedule === undefined) {
    // Old signature: createMaintenanceSchedule({ yacht_id, ...data })
    const { yacht_id, ...data } = yachtIdOrSchedule
    return createInSubcollection(yacht_id, 'maintenance_schedules', { ...data, yacht_id, created_at: now(), updated_at: now() })
  }
  // New signature: createMaintenanceSchedule(yachtId, schedule)
  return createInSubcollection(yachtIdOrSchedule, 'maintenance_schedules', { ...schedule, yacht_id: yachtIdOrSchedule, created_at: now(), updated_at: now() })
}

// Supports both: updateMaintenanceSchedule(yachtId, scheduleId, data) and updateMaintenanceSchedule(id, updates)
export const updateMaintenanceSchedule = async (a, b, c) => {
  if (c !== undefined) {
    // New: (yachtId, scheduleId, data)
    return updateInSubcollection(a, 'maintenance_schedules', b, c)
  }
  // Old: (id, updates)
  const yachtId = b._yachtId || b.yacht_id
  if (yachtId) {
    const { _yachtId, ...clean } = b
    return updateInSubcollection(yachtId, 'maintenance_schedules', a, clean)
  }
  console.error('updateMaintenanceSchedule: yacht_id required')
  return null
}

// Supports both: deleteMaintenanceSchedule(yachtId, scheduleId) and deleteMaintenanceSchedule(id, yachtId) — same args, different order intention but works either way
export const deleteMaintenanceSchedule = async (a, b) => {
  if (!a || !b) {
    // Single arg old style: deleteMaintenanceSchedule(id) — can't work without yachtId
    console.error('deleteMaintenanceSchedule: need both yachtId and scheduleId')
    return false
  }
  // Try new style first (yachtId, scheduleId), fall back
  return deleteFromSubcollection(a, 'maintenance_schedules', b)
}

export const getMaintenanceTasks = async (yachtId, filters = {}) => {
  if (!isFirebaseConfigured || !yachtId) return []
  try {
    const colRef = collection(db, 'yachts', yachtId, 'maintenance_tasks')
    const constraints = [orderBy('due_date', 'asc')]
    if (filters.status) constraints.push(where('status', '==', filters.status))
    if (filters.category) constraints.push(where('category', '==', filters.category))
    const q = query(colRef, ...constraints)
    const snap = await getDocs(q)
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
  } catch (error) {
    console.error('Error fetching maintenance tasks:', error)
    return []
  }
}

export const createMaintenanceTask = async (yachtId, task) => {
  return createInSubcollection(yachtId, 'maintenance_tasks', {
    ...task,
    yacht_id: yachtId,
    created_at: now(),
    updated_at: now()
  })
}

export const updateMaintenanceTask = async (yachtId, taskId, data) => {
  if (!yachtId) { console.error('updateMaintenanceTask: yachtId required'); return null }
  return updateInSubcollection(yachtId, 'maintenance_tasks', taskId, data)
}

export const deleteMaintenanceTask = async (yachtId, taskId) => {
  if (!yachtId) { console.error('deleteMaintenanceTask: yachtId required'); return false }
  return deleteFromSubcollection(yachtId, 'maintenance_tasks', taskId)
}

export const completeMaintenanceTask = async (yachtId, taskId, completionData) => {
  if (!isFirebaseConfigured || !yachtId) return null
  try {
    const taskRef = doc(db, 'yachts', yachtId, 'maintenance_tasks', taskId)
    await updateDoc(taskRef, {
      ...completionData,
      status: 'completed',
      completed_at: now(),
      updated_at: now()
    })
    return { id: taskId, ...completionData, status: 'completed' }
  } catch (error) {
    console.error('Error completing maintenance task:', error)
    return null
  }
}

export const getMaintenanceHistory = async (yachtId) => {
  if (!isFirebaseConfigured || !yachtId) return []
  try {
    const colRef = collection(db, 'yachts', yachtId, 'maintenance_tasks')
    const q = query(colRef, where('status', '==', 'completed'), orderBy('completed_at', 'desc'))
    const snap = await getDocs(q)
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
  } catch (error) {
    console.error('Error fetching maintenance history:', error)
    return []
  }
}

export const getMaintenanceStats = async (yachtId) => {
  if (!isFirebaseConfigured || !yachtId) return { total: 0, overdue: 0, dueSoon: 0, onTrack: 0, completed: 0, compliancePercent: 0 }
  try {
    const tasks = await getMaintenanceTasks(yachtId)
    const today = new Date().toISOString()
    const weekFromNow = new Date(Date.now() + 7 * 86400000).toISOString()

    const overdue = tasks.filter(t => t.status !== 'completed' && t.due_date < today).length
    const dueSoon = tasks.filter(t => t.status !== 'completed' && t.due_date >= today && t.due_date <= weekFromNow).length
    const completed = tasks.filter(t => t.status === 'completed').length
    const onTrack = tasks.filter(t => t.status !== 'completed' && t.due_date > weekFromNow).length
    const total = tasks.length
    const compliancePercent = total > 0 ? Math.round(((completed + onTrack) / total) * 100) : 100

    return { total, overdue, dueSoon, onTrack, completed, compliancePercent }
  } catch (error) {
    console.error('Error fetching maintenance stats:', error)
    return { total: 0, overdue: 0, dueSoon: 0, onTrack: 0, completed: 0, compliancePercent: 0 }
  }
}

// ============================================
// NOTIFICATIONS
// ============================================

export const getNotifications = async (userId, limitCount = 50) => {
  if (!isFirebaseConfigured || !userId) return []
  try {
    const colRef = collection(db, 'notifications')
    const q = query(colRef, where('userId', '==', userId), orderBy('createdAt', 'desc'), limit(limitCount))
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching notifications:', error)
    return []
  }
}

export const subscribeToNotifications = (userId, callback) => {
  if (!isFirebaseConfigured || !userId) return () => {}
  const colRef = collection(db, 'notifications')
  const q = query(colRef, where('userId', '==', userId), orderBy('createdAt', 'desc'), limit(50))
  return onSnapshot(q, (snap) => {
    callback(snapToArray(snap))
  }, (error) => {
    console.error('Error subscribing to notifications:', error)
  })
}

export const createNotification = async (notification) => {
  if (!isFirebaseConfigured) return null
  try {
    const colRef = collection(db, 'notifications')
    const docRef = await addDoc(colRef, {
      ...notification,
      read: false,
      createdAt: now()
    })
    return { id: docRef.id, ...notification, read: false }
  } catch (error) {
    console.error('Error creating notification:', error)
    return null
  }
}

export const markNotificationRead = async (notificationId) => {
  if (!isFirebaseConfigured) return false
  try {
    const docRef = doc(db, 'notifications', notificationId)
    await updateDoc(docRef, { read: true })
    return true
  } catch (error) {
    console.error('Error marking notification read:', error)
    return false
  }
}

export const markAllNotificationsRead = async (userId) => {
  if (!isFirebaseConfigured || !userId) return false
  try {
    const colRef = collection(db, 'notifications')
    const q = query(colRef, where('userId', '==', userId), where('read', '==', false))
    const snap = await getDocs(q)
    const updates = snap.docs.map(d => updateDoc(d.ref, { read: true }))
    await Promise.all(updates)
    return true
  } catch (error) {
    console.error('Error marking all notifications read:', error)
    return false
  }
}

export const getUnreadNotificationCount = async (userId) => {
  if (!isFirebaseConfigured || !userId) return 0
  try {
    const colRef = collection(db, 'notifications')
    const q = query(colRef, where('userId', '==', userId), where('read', '==', false))
    const snap = await getCountFromServer(q)
    return snap.data().count
  } catch (error) {
    console.error('Error getting unread count:', error)
    return 0
  }
}

export const deleteNotification = async (notificationId) => {
  if (!isFirebaseConfigured) return false
  try {
    await deleteDoc(doc(db, 'notifications', notificationId))
    return true
  } catch (error) {
    console.error('Error deleting notification:', error)
    return false
  }
}

export const createBulkNotifications = async (notifications) => {
  if (!isFirebaseConfigured) return []
  try {
    const colRef = collection(db, 'notifications')
    const results = await Promise.all(
      notifications.map(n => addDoc(colRef, { ...n, read: false, createdAt: now() }))
    )
    return results.map((ref, i) => ({ id: ref.id, ...notifications[i] }))
  } catch (error) {
    console.error('Error creating bulk notifications:', error)
    return []
  }
}

// ============================================
// CREW MESSAGING / CHAT
// ============================================

export const getConversations = async (yachtId) => {
  if (!isFirebaseConfigured || !yachtId) return []
  try {
    const colRef = collection(db, 'yachts', yachtId, 'conversations')
    const q = query(colRef, orderBy('lastMessageAt', 'desc'))
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching conversations:', error)
    return []
  }
}

export const subscribeToConversations = (yachtId, callback) => {
  if (!isFirebaseConfigured || !yachtId) return () => {}
  const colRef = collection(db, 'yachts', yachtId, 'conversations')
  const q = query(colRef, orderBy('lastMessageAt', 'desc'))
  return onSnapshot(q, (snap) => {
    callback(snapToArray(snap))
  }, (error) => {
    console.error('Error subscribing to conversations:', error)
  })
}

export const createConversation = async (yachtId, conversation) => {
  if (!isFirebaseConfigured || !yachtId) return null
  try {
    const colRef = collection(db, 'yachts', yachtId, 'conversations')
    const docRef = await addDoc(colRef, {
      ...conversation,
      lastMessage: '',
      lastMessageAt: now(),
      createdAt: now()
    })
    return { id: docRef.id, ...conversation }
  } catch (error) {
    console.error('Error creating conversation:', error)
    return null
  }
}

export const getMessages = async (yachtId, conversationId, limitCount = 100) => {
  if (!isFirebaseConfigured || !yachtId || !conversationId) return []
  try {
    const colRef = collection(db, 'yachts', yachtId, 'conversations', conversationId, 'messages')
    const q = query(colRef, orderBy('createdAt', 'asc'), limit(limitCount))
    const snap = await getDocs(q)
    return snapToArray(snap)
  } catch (error) {
    console.error('Error fetching messages:', error)
    return []
  }
}

export const subscribeToMessages = (yachtId, conversationId, callback) => {
  if (!isFirebaseConfigured || !yachtId || !conversationId) return () => {}
  const colRef = collection(db, 'yachts', yachtId, 'conversations', conversationId, 'messages')
  const q = query(colRef, orderBy('createdAt', 'asc'))
  return onSnapshot(q, (snap) => {
    callback(snapToArray(snap))
  }, (error) => {
    console.error('Error subscribing to messages:', error)
  })
}

export const sendMessage = async (yachtId, conversationId, message) => {
  if (!isFirebaseConfigured || !yachtId || !conversationId) return null
  try {
    const colRef = collection(db, 'yachts', yachtId, 'conversations', conversationId, 'messages')
    const msgData = { ...message, createdAt: now(), type: message.type || 'text' }
    const docRef = await addDoc(colRef, msgData)

    // Update conversation's lastMessage
    const convRef = doc(db, 'yachts', yachtId, 'conversations', conversationId)
    await updateDoc(convRef, {
      lastMessage: message.text?.substring(0, 100) || '',
      lastMessageAt: now(),
      lastMessageSenderId: message.senderId
    })

    return { id: docRef.id, ...msgData }
  } catch (error) {
    console.error('Error sending message:', error)
    return null
  }
}

export const markConversationRead = async (yachtId, conversationId, userId) => {
  if (!isFirebaseConfigured || !yachtId || !conversationId) return false
  try {
    const convRef = doc(db, 'yachts', yachtId, 'conversations', conversationId)
    await updateDoc(convRef, {
      [`lastRead_${userId}`]: now()
    })
    return true
  } catch (error) {
    console.error('Error marking conversation read:', error)
    return false
  }
}

export const getUnreadMessageCount = async (yachtId, userId) => {
  if (!isFirebaseConfigured || !yachtId || !userId) return 0
  try {
    const conversations = await getConversations(yachtId)
    let count = 0
    for (const conv of conversations) {
      const lastRead = conv[`lastRead_${userId}`]
      if (conv.lastMessageAt && (!lastRead || conv.lastMessageAt > lastRead) && conv.lastMessageSenderId !== userId) {
        count++
      }
    }
    return count
  } catch (error) {
    console.error('Error getting unread message count:', error)
    return 0
  }
}

// Trigger helpers (to be called from existing create functions)
export const notifyCrewForMaintenance = async (task, crewMembers) => {
  const notifications = crewMembers.map(member => ({
    userId: member.user_id || member.id,
    title: 'Maintenance Task',
    message: `Task "${task.title || task.name}" has been assigned to you`,
    type: 'maintenance',
    link: '/dashboard/maintenance'
  }))
  return createBulkNotifications(notifications)
}

export const notifyCrewForMessage = async (senderId, senderName, conversationName, recipientIds) => {
  const notifications = recipientIds
    .filter(id => id !== senderId)
    .map(userId => ({
      userId,
      title: 'New Message',
      message: `${senderName} sent a message in ${conversationName}`,
      type: 'message',
      link: '/dashboard/messages'
    }))
  return createBulkNotifications(notifications)
}

// Export db for direct access where needed
export { db }
